import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import { supabase } from '../supabaseClient';

const ProgressBar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  z-index: 20000;
  background: ${props => props.theme.colors.borderSubtle};
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${props => props.value || 0}%;
  background: ${props => props.theme.gradients.primary};
  box-shadow: ${props => props.theme.shadows.glow};
  transform-origin: left;
  transition: width 0.1s linear;
`;

const Page = styled.main`
  min-height: 100vh;
  background: ${props => props.theme.colors.background};
  padding: calc(${props => props.theme.spacing.xxxl} + 16px) ${props => props.theme.spacing.lg} ${props => props.theme.spacing.xxxl};
  position: relative;
  overflow: hidden;
`;

const Glow = styled.div`
  position: absolute;
  inset: -200px;
  background: ${props => props.theme.gradients.heroBackground};
  pointer-events: none;
  filter: blur(30px);
  z-index: 0;
`;

const Container = styled.div`
  max-width: 1150px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const BackLink = styled(Link)`
  color: ${props => props.theme.colors.accentPurple};
  font-weight: ${props => props.theme.typography.weights.semibold};
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const Meta = styled.div`
  color: ${props => props.theme.colors.subtextGray};
  font-size: 0.95rem;
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr 280px;
  gap: ${props => props.theme.spacing.xxl};
  align-items: start;

  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    grid-template-columns: 1fr;
  }
`;

const Hero = styled.header`
  border-radius: 24px;
  border: 1px solid ${props => props.theme.colors.borderSubtle};
  background: ${props => props.theme.colors.surfaceCard};
  backdrop-filter: blur(18px);
  box-shadow: ${props => props.theme.shadows.card};
  overflow: hidden;
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const HeroCover = styled.div`
  height: 280px;
  background: ${props => props.theme.gradients.subtle};
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.45) 100%);
    pointer-events: none;
  }
`;

const HeroBody = styled.div`
  padding: ${props => props.theme.spacing.xxl};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.xl};
  }
`;

const HeroTitle = styled.h1`
  font-size: clamp(2rem, 4vw, 3.25rem);
  letter-spacing: -0.03em;
  line-height: 1.1;
  margin-bottom: ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.textLight};
`;

const HeroSub = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${props => props.theme.spacing.md};
  flex-wrap: wrap;
`;

const Pill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 999px;
  background: ${props => props.theme.gradients.subtle};
  border: 1px solid ${props => props.theme.colors.borderSubtle};
  color: ${props => props.theme.colors.textDark};
  font-size: 0.9rem;
  font-weight: ${props => props.theme.typography.weights.medium};
`;

const Actions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
`;

const ActionButton = styled.button`
  padding: 10px 14px;
  border-radius: 12px;
  background: ${props => props.theme.colors.surfaceCard};
  border: 1px solid ${props => props.theme.colors.borderSubtle};
  box-shadow: ${props => props.theme.shadows.small};
  color: ${props => props.theme.colors.textLight};
  font-weight: ${props => props.theme.typography.weights.semibold};
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-1px);
    border-color: ${props => props.theme.colors.accentPurple};
    box-shadow: ${props => props.theme.shadows.card};
  }
`;

const ArticleCard = styled.article`
  background: ${props => props.theme.colors.surfaceCard};
  backdrop-filter: blur(18px);
  border: 1px solid ${props => props.theme.colors.borderSubtle};
  border-radius: 24px;
  padding: ${props => props.theme.spacing.xxl};
  box-shadow: ${props => props.theme.shadows.card};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.xl};
  }

  img {
    max-width: 100%;
    border-radius: 16px;
    margin: ${props => props.theme.spacing.lg} 0;
    box-shadow: ${props => props.theme.shadows.medium};
  }

  h1, h2, h3 {
    scroll-margin-top: 80px;
    margin-top: ${props => props.theme.spacing.xl};
    margin-bottom: ${props => props.theme.spacing.md};
    color: ${props => props.theme.colors.textLight};
  }

  h2 {
    color: ${props => props.theme.colors.accentPurple};
  }

  p {
    color: ${props => props.theme.colors.textDark};
    line-height: 1.85;
    margin-bottom: ${props => props.theme.spacing.md};
    font-size: 1.05rem;
  }

  a {
    color: ${props => props.theme.colors.accentPurple};
    text-decoration: underline;
    text-decoration-thickness: 2px;
    text-underline-offset: 3px;
  }

  code {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    background: ${props => props.theme.gradients.subtle};
    padding: 2px 6px;
    border-radius: 8px;
  }

  pre {
    background: ${props => props.theme.mode === 'dark' ? 'rgba(0, 0, 0, 0.4)' : 'rgba(45, 45, 45, 0.92)'};
    color: white;
    padding: ${props => props.theme.spacing.lg};
    border-radius: 16px;
    overflow-x: auto;
  }

  pre code {
    background: transparent;
    padding: 0;
    border-radius: 0;
    color: inherit;
  }

  ul, ol {
    padding-left: ${props => props.theme.spacing.xl};
    margin-bottom: ${props => props.theme.spacing.md};
    color: ${props => props.theme.colors.textDark};
  }

  li {
    margin-bottom: ${props => props.theme.spacing.xs};
  }

  blockquote {
    border-left: 4px solid ${props => props.theme.colors.accentPurple};
    padding-left: ${props => props.theme.spacing.lg};
    margin: ${props => props.theme.spacing.lg} 0;
    color: ${props => props.theme.colors.subtextGray};
    font-style: italic;
  }
`;

const Aside = styled.aside`
  position: sticky;
  top: 90px;
  align-self: start;

  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    position: static;
  }
`;

const TocCard = styled.div`
  background: ${props => props.theme.colors.surfaceCard};
  backdrop-filter: blur(16px);
  border: 1px solid ${props => props.theme.colors.borderSubtle};
  border-radius: 20px;
  padding: ${props => props.theme.spacing.lg};
  box-shadow: ${props => props.theme.shadows.card};
`;

const TocTitle = styled.div`
  font-weight: ${props => props.theme.typography.weights.bold};
  color: ${props => props.theme.colors.textLight};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const TocLink = styled.button`
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  padding: 10px;
  border-radius: 12px;
  color: ${props => props.theme.colors.subtextGray};
  transition: all 0.2s ease;
  font-size: 0.95rem;
  line-height: 1.35;
  cursor: pointer;

  &:hover {
    background: ${props => props.theme.gradients.subtle};
    color: ${props => props.theme.colors.textLight};
  }
`;

const Toast = styled(motion.div)`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  padding: 10px 14px;
  border-radius: 999px;
  background: ${props => props.theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(20, 20, 20, 0.85)'};
  color: ${props => props.theme.mode === 'dark' ? '#1a1a2e' : 'white'};
  font-size: 0.95rem;
  z-index: 20001;
`;

function slugify(text) {
  return (text || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 80);
}

function extractHeadings(markdown) {
  if (!markdown) return [];
  const lines = markdown.split('\n');
  const headings = [];
  for (const line of lines) {
    const m = line.match(/^(##|###)\s+(.+)$/);
    if (m) {
      const level = m[1] === '##' ? 2 : 3;
      const text = m[2].trim();
      headings.push({ level, text, id: slugify(text) });
    }
  }
  return headings;
}

function buildDescription(markdown) {
  if (!markdown) return '';
  const text = markdown
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/[`*_>#-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return text.length > 160 ? text.slice(0, 157) + '...' : text;
}

const BlogDetail = () => {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [toast, setToast] = useState('');
  const toastTimer = useRef(null);

  useEffect(() => {
    let mounted = true;
    const fetchBlog = async () => {
      if (!supabase) {
        setError('Blog service not configured');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const { data, error } = await supabase
          .from('blogs')
          .select('*')
          .eq('id', id)
          .eq('status', 'published')
          .single();

        if (error) throw error;
        if (mounted) setBlog(data);
      } catch (e) {
        if (mounted) setError(e.message || 'Failed to load blog');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (id) fetchBlog();
    return () => {
      mounted = false;
    };
  }, [id]);

  const canonicalUrl = useMemo(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return origin ? `${origin}/blog/${id}` : '';
  }, [id]);

  const title = blog?.title ? `${blog.title} | Neal by Continua Labs` : 'Blog | Neal by Continua Labs';
  const description = buildDescription(blog?.content);
  const ogImage = blog?.thumbnail_url || '';

  const headings = useMemo(() => extractHeadings(blog?.content), [blog?.content]);

  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const scrollTop = doc.scrollTop;
      const scrollHeight = doc.scrollHeight - doc.clientHeight;
      const pct = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;
      setProgress(Math.min(100, Math.max(0, pct)));
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const showToast = (msg) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), 1800);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      showToast('Link copied');
    } catch {
      showToast('Copy failed');
    }
  };

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: blog?.title || 'Blog', url: window.location.href });
      } else {
        await copyLink();
      }
    } catch {
      // user cancelled share
    }
  };

  const scrollToHeading = (headingId) => {
    const el = document.getElementById(headingId);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <ProgressBar aria-hidden="true">
        <ProgressFill value={progress} />
      </ProgressBar>

      <Page>
        <Glow />
        <Helmet>
          <title>{title}</title>
          {description && <meta name="description" content={description} />}
          {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

          {/* Open Graph */}
          <meta property="og:type" content="article" />
          <meta property="og:title" content={title} />
          {description && <meta property="og:description" content={description} />}
          {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
          {ogImage && <meta property="og:image" content={ogImage} />}

          {/* Twitter */}
          <meta name="twitter:card" content={ogImage ? 'summary_large_image' : 'summary'} />
          <meta name="twitter:title" content={title} />
          {description && <meta name="twitter:description" content={description} />}
          {ogImage && <meta name="twitter:image" content={ogImage} />}
        </Helmet>

        <Container>
          <TopBar>
            <BackLink to="/">← Back to Home</BackLink>
            <Actions>
              <ActionButton onClick={share} type="button">Share</ActionButton>
              <ActionButton onClick={copyLink} type="button">Copy link</ActionButton>
            </Actions>
          </TopBar>

          {loading && <Meta>Loading…</Meta>}
          {error && <Meta style={{ color: '#EF4444' }}>Error: {error}</Meta>}
          {!loading && !error && blog && (
            <>
              <Hero>
                {blog.thumbnail_url && (
                  <HeroCover>
                    <img src={blog.thumbnail_url} alt={blog.title} />
                  </HeroCover>
                )}
                <HeroBody>
                  <HeroTitle>{blog.title}</HeroTitle>
                  <HeroSub>
                    <Pill>
                      <span>{new Date(blog.created_at).toLocaleDateString()}</span>
                      <span>•</span>
                      <span>~3 min read</span>
                    </Pill>
                    {headings.length > 0 && (
                      <Pill>
                        <span>{headings.length} sections</span>
                      </Pill>
                    )}
                  </HeroSub>
                </HeroBody>
              </Hero>

              <Layout>
                <div>
                  <ArticleCard>
                    <ReactMarkdown
                      components={{
                        h2: ({ node, children, ...props }) => {
                          const text = String(children?.[0] ?? '').trim();
                          const hid = slugify(text);
                          return (
                            <h2 id={hid} {...props}>
                              {children}
                            </h2>
                          );
                        },
                        h3: ({ node, children, ...props }) => {
                          const text = String(children?.[0] ?? '').trim();
                          const hid = slugify(text);
                          return (
                            <h3 id={hid} {...props}>
                              {children}
                            </h3>
                          );
                        },
                      }}
                    >
                      {blog.content}
                    </ReactMarkdown>
                  </ArticleCard>
                </div>

                <Aside>
                  {headings.length > 0 && (
                    <TocCard>
                      <TocTitle>On this page</TocTitle>
                      {headings.map((h) => (
                        <TocLink
                          key={h.id}
                          type="button"
                          onClick={() => scrollToHeading(h.id)}
                          style={{ paddingLeft: h.level === 3 ? 18 : 10 }}
                        >
                          {h.text}
                        </TocLink>
                      ))}
                    </TocCard>
                  )}
                </Aside>
              </Layout>
            </>
          )}
        </Container>

        <AnimatePresence>
          {toast && (
            <Toast
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
            >
              {toast}
            </Toast>
          )}
        </AnimatePresence>
      </Page>
    </>
  );
};

export default BlogDetail;
